import notification from "antd/es/notification";
import { uniq } from "lodash-es";
import { defineMessages } from "react-intl";
import { call, put, select } from "redux-saga/effects";

import Address from "@mapmycustomers/shared/types/Address";
import { GeocodeResult } from "@mapmycustomers/shared/types/base/Located";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import { MapLeadsResponse } from "@mapmycustomers/shared/types/map";
import Organization from "@mapmycustomers/shared/types/Organization";
import PersistentLeadFinderSettings from "@mapmycustomers/shared/types/persistent/PersistentLeadFinderSettings";
import {
  convertCoordinatesToGeoPoint,
  convertLatLngToCoordinates,
  convertPlatformBoundsToLatLngBounds,
} from "@mapmycustomers/shared/util/geo/GeoService";
import { showModal } from "@mapmycustomers/ui";

import {
  getLeadFinderMode,
  getLeadFinderProcessing,
  getLeadFinderQuery,
  getLeadFinderRecords,
  getLeadFinderSearchDistance,
  getLeadFinderSettings,
} from "@app/component/leadFinder/store/selectors";
import {
  LEAD_FINDER_QUERY_LIMIT_FIRST,
  LEAD_FINDER_QUERY_LIMIT_FOLLOWING,
} from "@app/component/leadFinder/utils/consts";
import i18nService from "@app/config/I18nService";
import localSettings from "@app/config/LocalSettings";
import AreaSearchMode from "@app/enum/AreaSearchMode";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import {
  doesOrganizationSupportTerritories,
  getOrganizationId,
  getPosition,
  isLeadFinderSaveEnabled,
} from "@app/store/iam";
import { fetchAllTerritories } from "@app/store/territories/actions";
import LeadFinderLimit from "@app/types/map/LeadFinderLimit";
import { METERS_IN_MILE } from "@app/util/consts";
import {
  FIRST_WARNING_LIMIT,
  SECOND_WARNING_LIMIT,
  THIRD_WARNING_LIMIT,
} from "@app/util/leadFinder/showLeadFinderLimitAlert";
import { leadIdGetter } from "@app/util/map/idGetters";

import {
  clearLeadFinderRecords,
  geocodeLocation,
  setCenterPoint,
  setConfigurationVisibility,
  setSearchHistory,
  startLeadFinderSearch,
} from "../actions";
import { LeadFinderQueryState } from "../LeadFinderState";

const messages = defineMessages({
  noNewLeadsDescription: {
    id: "leadFinder.store.notifications.search.noMoreLeads.description",
    defaultMessage:
      "There are no more new leads in this area. Try panning the map to search for leads in another location, or adjust your search.",
    description: "No more leads modal description",
  },
  noNewLeadsTitle: {
    id: "leadFinder.store.notifications.search.noMoreLeads.title",
    defaultMessage: "No more leads in this area",
    description: "No more leads modal title",
  },
});

export function* onStartLeadFinderSearch({
  payload,
}: ReturnType<typeof startLeadFinderSearch.request>) {
  try {
    const intl = i18nService.getIntl();
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const viewport = payload.viewport;
    const mode: AreaSearchMode = yield select(getLeadFinderMode);
    const query: LeadFinderQueryState = yield select(getLeadFinderQuery);
    if (!query.search) {
      return;
    }
    const currentLimit: LeadFinderLimit = yield callApi("fetchLeadFinderLimits", orgId);
    const { orgLimit, userLimit } = currentLimit;
    const isOrgLimitReached = orgLimit.percentageConsumed >= 100 && orgLimit.limit !== 0;
    const isUserLimitReached =
      userLimit.percentageConsumed >= 100 && userLimit.userLeadGenCapEnabled;

    const previousLimit = localSettings.getRecentLeadFinderLimit();
    if (previousLimit && payload.onLimitThresholdReached) {
      if (
        (orgLimit.percentageConsumed < previousLimit.orgLimit.percentageConsumed &&
          orgLimit.limit !== 0) ||
        (userLimit.percentageConsumed < previousLimit.userLimit.percentageConsumed &&
          userLimit.userLeadGenCapEnabled)
      ) {
        // limit was reset, we don't need to do anything here
      } else if (
        (orgLimit.limit !== 0 &&
          ((previousLimit.orgLimit.percentageConsumed < FIRST_WARNING_LIMIT &&
            orgLimit.percentageConsumed >= FIRST_WARNING_LIMIT) ||
            (previousLimit.orgLimit.percentageConsumed < SECOND_WARNING_LIMIT &&
              orgLimit.percentageConsumed >= SECOND_WARNING_LIMIT) ||
            (previousLimit.orgLimit.percentageConsumed < THIRD_WARNING_LIMIT &&
              orgLimit.percentageConsumed >= THIRD_WARNING_LIMIT))) ||
        (userLimit.userLeadGenCapEnabled &&
          ((previousLimit.userLimit.percentageConsumed < FIRST_WARNING_LIMIT &&
            userLimit.percentageConsumed >= FIRST_WARNING_LIMIT) ||
            (previousLimit.userLimit.percentageConsumed < SECOND_WARNING_LIMIT &&
              userLimit.percentageConsumed >= SECOND_WARNING_LIMIT) ||
            (previousLimit.userLimit.percentageConsumed < THIRD_WARNING_LIMIT &&
              userLimit.percentageConsumed >= THIRD_WARNING_LIMIT)))
      ) {
        yield call(payload.onLimitThresholdReached, currentLimit);
        yield put(startLeadFinderSearch.failure());
        return;
      }
    }

    localSettings.setRecentLeadFinderLimit(currentLimit);

    if (isOrgLimitReached || isUserLimitReached) {
      if (payload.onLimitThresholdReached) {
        yield call(payload.onLimitThresholdReached, currentLimit);
      }
      yield put(startLeadFinderSearch.failure());
      return;
    }

    payload.onBeforeSearch?.();
    yield put(setConfigurationVisibility({ visible: false }));
    if (!payload.refresh) {
      yield put(clearLeadFinderRecords());
    }

    // Add submitted search query to recent queries history
    // If save query present in saved searches - do not include. Also do not allow duplicates in recent history
    const savedSearchesEnabled: boolean = yield select(isLeadFinderSaveEnabled);
    const configuration: PersistentLeadFinderSettings = yield select(getLeadFinderSettings);
    const savedSearches = savedSearchesEnabled ? configuration.savedSearches : [];
    const recentSearches = savedSearches.includes(query.search)
      ? configuration.recentSearches
      : uniq([query.search, ...configuration.recentSearches]);
    const modifiedConfiguration = {
      recentSearches,
      savedSearches,
    };
    yield put(setSearchHistory(modifiedConfiguration));

    let point: LongLat = query.point ?? [0, 0];

    if (payload.refresh) {
      // If we're refreshing - always fetch leads around viewport center, because it has no sense to
      // request leads search around original point - probably there are no more results would be returned
      if (viewport?.bounds) {
        const center = convertPlatformBoundsToLatLngBounds(viewport.bounds).getCenter();
        const geoPoint = convertCoordinatesToGeoPoint(convertLatLngToCoordinates(center));
        point = geoPoint.coordinates;
      }
    } else {
      switch (mode) {
        case AreaSearchMode.ADDRESS:
          if (!query.point) {
            const addressToCheck = {
              address: query.address?.address ?? "",
              city: query.address?.city ?? "",
              country: query.address?.country ?? "",
              postalCode: query.address?.postalCode ?? "",
              region: query.address?.region ?? "",
              regionCode: query.address?.regionCode ?? "",
            };
            const response: GeocodeResult = yield callApi("geocodeAddress", orgId, addressToCheck);
            if (response.geoPoint) {
              point = response.geoPoint.coordinates;
            } else {
              notification.error({
                message: intl?.formatMessage({
                  id: "leadFinder.store.notifications.search.error.geocodingIssue",
                  defaultMessage: "Failed to find location",
                  description: "Failed to find location in Lead Finder",
                }),
              });
              yield put(startLeadFinderSearch.failure());
              return;
            }
          }
          break;

        case AreaSearchMode.CURRENT_USER_LOCATION: {
          const position: GeolocationPosition | undefined = yield select(getPosition);
          if (
            position?.coords?.longitude !== undefined &&
            position?.coords?.latitude !== undefined
          ) {
            point = [position.coords.longitude, position.coords.latitude];
          }
          break;
        }

        case AreaSearchMode.ENTITY:
          break;

        default:
          if (viewport?.bounds) {
            const center = convertPlatformBoundsToLatLngBounds(viewport.bounds).getCenter();
            const geoPoint = convertCoordinatesToGeoPoint(convertLatLngToCoordinates(center));
            point = geoPoint.coordinates;
          }
          break;
      }
    }

    const searchDistance: number = yield select(getLeadFinderSearchDistance);
    const radius = searchDistance * (localSettings.doesUseSiUnits() ? 1000 : METERS_IN_MILE);

    const requestPayload = {
      $filters: {
        point,
        query: query.search,
        radius,
      },
      // For refresh load up to 20 new leads
      $limit: payload.refresh ? LEAD_FINDER_QUERY_LIMIT_FOLLOWING : LEAD_FINDER_QUERY_LIMIT_FIRST,
    };

    const territoriesEnabled: boolean = yield select(doesOrganizationSupportTerritories);
    if (territoriesEnabled) {
      // we need to fetch territories in order to know their count
      // In order to process "isInTerr" flag correctly.
      yield put(fetchAllTerritories.request());
    }

    const response: MapLeadsResponse = yield callApi("fetchLeads", orgId, requestPayload);
    yield put(setCenterPoint(point));

    // Check if still processing request (e.g. not cancelled)
    const isProcessing: boolean = yield select(getLeadFinderProcessing);
    if (isProcessing) {
      const showNoMoreLeadsModal = () =>
        showModal({
          cancelButtonProps: { hidden: true },
          centered: true,
          content: intl?.formatMessage(messages.noNewLeadsDescription),
          title: intl?.formatMessage(messages.noNewLeadsTitle),
          type: "info",
          width: "31.25rem",
        });

      if (payload.refresh) {
        const records: MapLeadsResponse["data"] = yield select(getLeadFinderRecords);

        const newRecords = response.data ?? [];
        const newRecordsMap = new Set(newRecords.map((lead) => leadIdGetter(lead)));
        const combinedRecords = [
          ...newRecords,
          ...records.filter((lead) => !newRecordsMap.has(leadIdGetter(lead))),
        ];

        const result = {
          records: combinedRecords,
          totalFilteredRecords: newRecords.length,
          totalRecords: newRecords.length,
        };

        if (response.total === 0 || combinedRecords.length === records.length) {
          showNoMoreLeadsModal();
        }

        yield put(startLeadFinderSearch.success(result));

        if (payload.callback) {
          // Calculate bounding area of all records in the list and send it to callback
          const bounds = new google.maps.LatLngBounds();
          combinedRecords.forEach((lead) => {
            if (lead.geometry?.location) {
              bounds.extend(lead.geometry.location);
            }
          });

          payload.callback(bounds.isEmpty() ? undefined : bounds);
        }
      } else {
        const records = response.data ?? [];
        const result = {
          records,
          totalFilteredRecords: response.count,
          totalRecords: response.total,
        };

        if (response.total === 0) {
          showNoMoreLeadsModal();
        }

        yield put(startLeadFinderSearch.success(result));

        // Calculate bounding area of all records in the list
        const bounds = new google.maps.LatLngBounds();
        records.forEach((lead) => {
          if (lead.geometry?.location) {
            bounds.extend(lead.geometry.location);
          }
        });
        const foundBounds = bounds.isEmpty() ? undefined : bounds;

        if (payload.callback) {
          payload.callback(foundBounds);
        }

        payload.onSearchSuccess?.(foundBounds);
      }
    }
  } catch (error) {
    yield put(startLeadFinderSearch.failure());
    yield put(handleError({ error }));
  }
}

export function* onGeocodeLocation({ payload }: ReturnType<typeof geocodeLocation.request>) {
  if (!payload.point) {
    return;
  }

  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const result: GeocodeResult = yield callApi("reverseGeocodeAddress", orgId, {
      coordinates: payload.point,
      type: "Point",
    });

    const address = result.address as Address;
    const location = address.formattedAddress ?? "";

    yield put(geocodeLocation.success({ address, location }));
  } catch (error) {
    yield put(geocodeLocation.failure());
    yield put(handleError({ error }));
  }
}
